import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CalendarBlank, Plus, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HolidayManagementPage() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ date: '', reason: '', pause_orders: true });

  useEffect(() => { fetchHolidays(); }, []);

  const fetchHolidays = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/holidays`, { withCredentials: true });
      setHolidays(data.holidays || []);
    } catch { toast.error('Failed to load holidays'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.date) { toast.error('Please select a date'); return; }
    try {
      await axios.post(`${API}/api/seller/holidays`, form, { withCredentials: true });
      toast.success('Holiday added');
      setDialogOpen(false);
      setForm({ date: '', reason: '', pause_orders: true });
      fetchHolidays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add holiday'); }
  };

  const handleDelete = async (hid) => {
    try {
      await axios.delete(`${API}/api/seller/holidays/${hid}`, { withCredentials: true });
      toast.success('Holiday removed');
      fetchHolidays();
    } catch { toast.error('Failed to delete'); }
  };

  const isPast = (d) => new Date(d) < new Date(new Date().toDateString());

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl" data-testid="holiday-management-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Holiday Management</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Mark holidays to pause order processing</p>
          </div>
          <Button data-testid="add-holiday-btn" onClick={() => setDialogOpen(true)} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Holiday
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : holidays.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <CalendarBlank size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No holidays configured. Orders will be processed every day.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {holidays.map(h => (
              <div key={h.holiday_id} className={`flex items-center justify-between px-5 py-4 bg-white rounded-xl border ${isPast(h.date) ? 'border-[#e5e5ea] opacity-60' : 'border-[#7d64ff]/20'}`} data-testid={`holiday-${h.holiday_id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#7d64ff]/10 flex items-center justify-center">
                    <CalendarBlank size={22} className="text-[#7d64ff]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A0A0A]">{new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <p className="text-sm text-[#6b6b8a]">{h.reason || 'No reason specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {h.pause_orders && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Orders Paused</span>}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(h.holiday_id)} className="text-red-500 hover:text-red-700" data-testid={`delete-holiday-${h.holiday_id}`}><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="holiday-dialog">
            <DialogHeader><DialogTitle>Add Holiday</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Date</Label><Input data-testid="holiday-date" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
              <div><Label>Reason</Label><Input data-testid="holiday-reason" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Diwali, Republic Day" /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.pause_orders} onChange={e => setForm(f => ({ ...f, pause_orders: e.target.checked }))} className="rounded" />
                Pause order processing on this date
              </label>
              <Button data-testid="save-holiday-btn" onClick={handleAdd} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">Add Holiday</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
